import { useSecurity } from '@ucc/react/security';
import { Viewer } from '~/gql/ucc/validation';
import { Permission as UccPermission } from '~/gql/ucc/graphql';
import { GetEmployeeDocument, ListSubprojectMetadataDocument, Projekt } from '~/gql/ucpw/graphql';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import * as graphql from '@ucc/graphql/documents/roles';

export type Permission = 'view' | 'create' | 'update' | 'delete' | 'enabled';
export type Resource =
    | 'project.project'
    | 'project.residentialUnits'
    | 'project.participants'
    | 'project.tasks'
    | 'project.correspondence'
    | 'project.emergencies'
    | 'project.report'
    | 'project.schedule'
    | 'project.deviceUsage'
    | 'project.measures'
    | 'project.documents'
    | 'finance.invoice'
    | 'finance.offer'
    | 'finance.calculation'
    | 'dashboard.openTasks'
    | 'dashboard.contractInbox'
    | 'masterData.controlling'
    | 'masterData.deviceType'
    | 'masterData.trade'
    | 'masterData.contacts'
    | 'masterData.branches'
    | 'masterData.projectType'
    | 'masterData.agreements'
    | 'masterData.others'
    | 'masterData.textSnippets'
    | 'masterData.insurances'
    | 'masterData.function'
    | 'masterData.services'
    | 'staff.permissions'
    | 'staff.employees'
    | 'staff.absences'
    | 'additional.editAllCorrespondence'
    | 'additional.editAllTasks'
    | 'additional.reopenTask'
    | 'additional.downloadProjects'
    | 'additional.downloadContacts'
    | 'additional.downloadInvoice'
    | 'additional.createInvoice'
    | 'additional.releaseInvoice'
    | 'additional.releaseOffer'
    | 'additional.onlyShowOwnEvents'
    | 'pxconnect.terminerfassung'
    | 'pxconnect.schadenbericht';

function usePermissions(uccPermissions: UccPermission[]) {
    const permissions: Record<string, string[]> = uccPermissions.reduce(
        (acc, permission) => ({ ...acc, [permission.id]: permission.permissions }),
        {}
    );

    const getPermission = (resource: string) => permissions[resource];

    const hasPermission = <R extends Resource, P extends Permission>(
        resource: R,
        permission: P
    ) => {
        if (!resource) {
            return false;
        }
        const resourcePermissions = getPermission(resource);
        return resourcePermissions?.includes(permission);
    };

    const isReadOnly = <R extends Resource>(resource: R) => {
        if (!resource) {
            return false;
        }

        const resourcePermissions = getPermission(resource);
        // to mony permissions
        if (resourcePermissions?.length > 1) {
            return false;
        }

        return resourcePermissions?.includes('view');
    };

    return {
        getPermission,
        hasPermission,
        isReadOnly,
    };
}

function useViewerOriginal() {
    const { viewer } = useSecurity();

    const { data, loading } = useQuery(GetEmployeeDocument, {
        variables: { id: viewer?.app?.mitarbeiterId },
        skip: !Boolean(viewer?.app?.mitarbeiterId),
        context: { clientName: 'ucpw' },
    });

    const employee: any | null = data?.item?.item || null;

    const { getPermission, hasPermission, isReadOnly } = usePermissions(
        (viewer as Viewer)?.permissions
    );

    return {
        ...(viewer as Viewer),
        ...(employee && { employee, role: employee?.rolle, roleId: employee?.rolleId }),
        getPermission,
        hasPermission,
        isReadOnly,
        loading,
    };
}

export function useViewer() {
    const { permissions, isUndefined, ...minimal } = useMinimal();
    const { hasPermission, getPermission, isReadOnly, ...viewer } = useViewerOriginal();
    const restrictedPermissions = usePermissions([
        ...(viewer as Viewer)?.permissions,
        ...permissions,
    ]);
    const location = useLocation();

    const projectIdMatch = location.pathname.match(/^\/projekte\/(\d+)/);
    const projectId = projectIdMatch ? parseInt(projectIdMatch[1], 10) : null;

    const { data, loading } = useQuery(ListSubprojectMetadataDocument, {
        variables: { id: projectId as number },
        context: { clientName: 'ucpw' },
        skip: !projectId,
    });
    const project = data?.item?.item as Projekt;
    const subprojects = project?.subprojekt || [];
    const isRejectedGDV = subprojects.some(
        (subproject) =>
            subproject?.metadata?.includes?.('serviceRequestId') &&
            subproject?.statusSnippet?.name === 'Abgelehnt'
    );

    // While the roles list or the project metadata are still in flight, the answer below is
    // provisional (the Minimal role stands in) — indistinguishable from "denied" for callers
    // that only read hasPermission. Exposed so a gate can show a third state instead of a false no.
    const permissionsLoading = minimal.loading || (!isUndefined && loading);

    if (!isUndefined && (loading || minimal.loading || isRejectedGDV)) {
        return { ...viewer, permissions, ...restrictedPermissions, permissionsLoading };
    }

    return { ...viewer, hasPermission, getPermission, isReadOnly, permissionsLoading };
}

function useMinimal(): {
    loading: boolean;
    permissions: any[];
    isUndefined: boolean;
} {
    const { data, loading } = useQuery(graphql.listRoles, {
        fetchPolicy: 'cache-first',
    });
    const roles = data?.security?.roles?.data || [];
    const minimal = roles.find((role) => role.name === 'Minimal');
    const isUndefined = minimal === undefined;

    const permissionGroups = minimal?.permissionGroups || [];
    const permissions =
        permissionGroups.flatMap((pg) =>
            pg.resources.map(({ id, permissions }) => ({ id, permissions }))
        ) || [];

    return { loading, permissions, isUndefined };
}
